import React, {
  useMemo,
  useState,
  useEffect,
  ComponentType,
  useCallback,
  ReactNode,
} from 'react';

import PropTypes from 'prop-types';

import { getAdminConstructionApiUrl } from '@adsk/acs-sc-web-platform-api';
import { useGetAllPaginatedData, CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useContextWithValidation,
} from '@adsk/alloy-react-context';

import { useCurrentProjectId } from './CurrentProjectIdContext';
import { useDeletedUsers } from './DeletedUsers';
import { removeOldDuplicatesBy } from './helpers/removeOldDuplicatesBy';
import useClientSideDataModification from './hooks/useClientSideDataModification';
import { Pagination, User } from './types';

export type UsersContextValue = {
  data: User[];
  loading: boolean;
  error?: null | Error;
  retry(): void;
  addUsers(users: User[]): void;
  updateUser(user: User): void;
  removeUserById(id: User['id']): void;
  removeUserByAutodeskId(autodeskId: User['autodeskId']): void;
  setEnabled?: (enabled: boolean) => void;
};

type UsersResponse = {
  pagination: Pagination;
  results: User[];
};

type FindUserByOptions = {
  includeDeleted?: boolean;
};

/**
 * Context for users list.
 */
const UsersContext = getOrCreateGlobalContext<UsersContextValue>(
  CONTEXT_NAMES.UsersContext,
);

const useUsersContext = () =>
  useContextWithValidation(UsersContext, {
    hook: 'useUsers',
    provider: 'UsersProvider',
  });

export const useUsers = () => {
  const { setEnabled, ...rest } = useUsersContext();
  useEffect(() => {
    if (setEnabled) {
      setEnabled(true);
    }
  }, [setEnabled]);
  return rest;
};

const useFindUserBy = (
  matcher: (user: User) => boolean,
  options: FindUserByOptions = {},
) => {
  const { includeDeleted } = options;
  const {
    data: users,
    loading: areUsersLoading,
    error: usersError,
  } = useUsers();

  const {
    data: deletedUsers,
    loading: areDeletedUsersLoading,
    error: deletedUsersError,
  } = useDeletedUsers({ disable: !includeDeleted });

  const activeUser = useMemo(() => users.find(matcher), [users, matcher]);
  const shouldSearchDeletedUser = !activeUser && includeDeleted;
  const deletedUser = useMemo(
    () => (shouldSearchDeletedUser ? deletedUsers.find(matcher) : undefined),
    [shouldSearchDeletedUser, deletedUsers, matcher],
  );
  const user = activeUser || deletedUser;
  return {
    user,
    loading:
      !user && (areUsersLoading || (includeDeleted && areDeletedUsersLoading)),
    error: !user && (usersError || (includeDeleted && deletedUsersError)),
  };
};

export const useUserById = (id: string, options: FindUserByOptions = {}) => {
  const matcher = useCallback((user: User) => user.id === id, [id]);
  return useFindUserBy(matcher, options);
};

export const useUserByAutodeskId = (
  autodeskId: string,
  options: FindUserByOptions = {},
) => {
  const matcher = useCallback(
    (user: User) => user.autodeskId === autodeskId,
    [autodeskId],
  );
  return useFindUserBy(matcher, options);
};

const PAGE_LIMIT = 200;

const handlePendingUsers = (users: User[]) =>
  users.map((user) => ({
    ...user,
    name:
      user.status === 'pending' || user.name === 'New Member'
        ? user.email
        : user.name,
  }));

type UsersProviderProps = {
  children?: ReactNode;
};

export const UsersProvider: ComponentType<UsersProviderProps> = ({
  children,
}) => {
  const [enabled, setEnabled] = useState(false);
  const projectId = useCurrentProjectId();
  const adminApiUrl = getAdminConstructionApiUrl();

  const url =
    enabled && projectId
      ? `${adminApiUrl}/v1/projects/${projectId}/users?limit=${PAGE_LIMIT}`
      : '';

  const {
    loading: loadingData,
    error,
    data: dataFromBE,
    retry,
    clearSessionStorage,
  } = useGetAllPaginatedData<UsersResponse>({
    url,
  });

  const usersFromBE = useMemo(() => {
    return (
      dataFromBE &&
      removeOldDuplicatesBy(
        dataFromBE.flatMap((usersPages) =>
          handlePendingUsers(usersPages.results),
        ),
        (item) => item.id,
      )
    );
  }, [dataFromBE]);

  const {
    modifyData,
    addData: addUsers,
    updateEntity: updateUser,
    removeEntityById: removeUserById,
  } = useClientSideDataModification<User>(clearSessionStorage);

  const loading = loadingData || !enabled;
  const data = useMemo(
    () => modifyData(usersFromBE),
    [modifyData, usersFromBE],
  );

  const removeUserByAutodeskId = useCallback(
    (autodeskId: User['autodeskId']) => {
      const user = data.find((user) => user.autodeskId === autodeskId);
      if (user) {
        removeUserById(user.id);
      }
    },
    [data, removeUserById],
  );

  const value = useMemo(
    () => ({
      data,
      loading,
      error,
      retry,
      addUsers,
      updateUser,
      removeUserById,
      removeUserByAutodeskId,
      setEnabled: enabled ? undefined : setEnabled,
    }),
    [
      enabled,
      addUsers,
      updateUser,
      data,
      error,
      loading,
      removeUserById,
      removeUserByAutodeskId,
      retry,
    ],
  );

  return (
    <UsersContext.Provider value={value}>{children}</UsersContext.Provider>
  );
};

UsersProvider.displayName = 'UsersProvider';

UsersProvider.propTypes = {
  /** Nodes with access to context */
  children: PropTypes.node,
};
